import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Landing from './pages/Landing'; // CareerBoost Landing
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Resumes from './pages/Resumes';
import ResumeEditor from './pages/ResumeEditor';
import Jobs from './pages/Jobs';
import ContentRepurposer from './pages/ContentRepurposer';
import Pricing from './pages/Pricing';
import PaymentSuccess from './pages/PaymentSuccess';

// BizPlanAI Pages
import BizPlanLanding from './pages/BizPlanLanding';
import BizPlanGenerator from './pages/BizPlanGenerator';
import BizPlanDashboard from './pages/BizPlanDashboard';
import BizPlanView from './pages/BizPlanView';
import BizPlanPricing from './pages/BizPlanPricing';

// CreditBoost Pages
import CreditBoostLanding from './pages/CreditBoostLanding';
import CreditBoostGenerator from './pages/CreditBoostGenerator';
import CreditBoostDashboard from './pages/CreditBoostDashboard';
import CreditBoostPlanView from './pages/CreditBoostPlanView';
import CreditBoostDispute from './pages/CreditBoostDispute';
import CreditBoostPricing from './pages/CreditBoostPricing';

// Legal Pages
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

// Launch & Marketing
import LaunchCountdown from './pages/LaunchCountdown';
import LaunchCopy from './pages/LaunchCopy';
import HeroPreview from './pages/HeroPreview';

// Password Reset
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Spanish Pages
import UpliftHomeES from './pages/UpliftHomeES';

// Uplift Master Brand
import UpliftHome from './pages/UpliftHome';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Uplift Master Brand - Main Entry Point */}
          <Route path="/" element={<UpliftHome />} />
          
          {/* Public Routes */}
          <Route path="/careerboost" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes"
            element={
              <ProtectedRoute>
                <Resumes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes/new"
            element={
              <ProtectedRoute>
                <ResumeEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes/:id"
            element={
              <ProtectedRoute>
                <ResumeEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes/:id/edit"
            element={
              <ProtectedRoute>
                <ResumeEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/new"
            element={
              <ProtectedRoute>
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/repurpose"
            element={
              <ProtectedRoute>
                <ContentRepurposer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <Pricing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Legal Routes */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Launch & Marketing Routes */}
          <Route path="/launch" element={<LaunchCountdown />} />
          <Route path="/coming-soon" element={<LaunchCountdown />} />
          <Route path="/copy" element={<LaunchCopy />} />
          <Route path="/hero-preview" element={<HeroPreview />} />

          {/* Spanish Routes */}
          <Route path="/es" element={<UpliftHomeES />} />
          <Route path="/es/careerboost" element={<UpliftHomeES />} />
          <Route path="/es/bizplanai" element={<UpliftHomeES />} />
          <Route path="/es/creditboost" element={<UpliftHomeES />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />

          {/* BizPlanAI Routes */}
          <Route path="/bizplanai" element={<BizPlanLanding />} />
          <Route path="/bizplanai/pricing" element={<BizPlanPricing />} />
          <Route
            path="/bizplanai/dashboard"
            element={
              <ProtectedRoute>
                <BizPlanDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bizplanai/generate"
            element={
              <ProtectedRoute>
                <BizPlanGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bizplanai/plans/:planId"
            element={
              <ProtectedRoute>
                <BizPlanView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bizplanai/payment/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />

          {/* CreditBoost Routes */}
          <Route path="/creditboost" element={<CreditBoostLanding />} />
          <Route path="/creditboost/pricing" element={<CreditBoostPricing />} />
          <Route
            path="/creditboost/dashboard"
            element={
              <ProtectedRoute>
                <CreditBoostDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creditboost/generate"
            element={
              <ProtectedRoute>
                <CreditBoostGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creditboost/plans/:planId"
            element={
              <ProtectedRoute>
                <CreditBoostPlanView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creditboost/dispute"
            element={
              <ProtectedRoute>
                <CreditBoostDispute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creditboost/payment/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
